import { appIntl } from '@components/locale/IntlGlobalProvider';

export enum UnitTypes {
  HEIGHT = 'HEIGHT',
  DISTANCE = 'DISTANCE',
  SPEED = 'SPEED',
  TIME = 'TIME',
}

export enum SpeedUnits {
  KILOMETERS_PER_HOUR = 'KILOMETERS_PER_HOUR',
  METERS_PER_SECOND = 'METERS_PER_SECOND',
  MILES_PER_HOUR = 'MILES_PER_HOUR',
  KNOTS = 'KNOTS',
}

export enum HeightUnits {
  METERS = 'METERS',
  FEET = 'FEET',
}

export enum DistanceUnits {
  METERS = 'METERS',
  KILOMETERS = 'KILOMETERS',
  MILES = 'MILES',
  NAUTICAL_MILES = 'NAUTICAL_MILES',
}

export enum AngleUnits {
  TURN = 'TURN',
}

export enum OtherUnits {
  PERCENT = 'PERCENT',
}

export enum WeightUnits {
  KILOGRAMS = 'KILOGRAMS',
}

export enum TemperatureUnits {
  CELCIUS = 'CELCIUS',
}

export enum PressureUnits {
  HECTOPASCAL = 'HECTOPASCAL',
}

export enum VoltageUnits {
  VOLT = 'VOLT',
}

export enum TimeUnits {
  MILLI_SECONDS = 'MILLI_SECONDS',
  SECONDS = 'SECONDS',
  MINUTES = 'MINUTES',
  HOURS = 'HOURS',
  DAYS = 'DAYS',
}

export type Units =
  | AngleUnits
  | DistanceUnits
  | HeightUnits
  | SpeedUnits
  | WeightUnits
  | OtherUnits
  | TemperatureUnits
  | PressureUnits
  | VoltageUnits
  | TimeUnits;

export type UnitTypeMap = {
  [UnitTypes.HEIGHT]: HeightUnits;
  [UnitTypes.DISTANCE]: DistanceUnits;
  [UnitTypes.SPEED]: SpeedUnits;
  [UnitTypes.TIME]: TimeUnits;
};

export const UnitValuesByType = {
  [UnitTypes.HEIGHT]: Object.values(HeightUnits),
  [UnitTypes.DISTANCE]: Object.values(DistanceUnits),
  [UnitTypes.SPEED]: Object.values(SpeedUnits),
  [UnitTypes.TIME]: Object.values(TimeUnits),
} satisfies {
  [U in UnitTypes]: UnitTypeMap[U][];
};

export const DefaultUnitValueByType = {
  [UnitTypes.HEIGHT]: HeightUnits.METERS,
  [UnitTypes.DISTANCE]: DistanceUnits.METERS,
  [UnitTypes.SPEED]: SpeedUnits.METERS_PER_SECOND,
  [UnitTypes.TIME]: TimeUnits.MILLI_SECONDS,
} satisfies {
  [K in UnitTypes]: UnitTypeMap[K];
};

export const unitLabel: { [key in Units]: string } = {
  [AngleUnits.TURN]: '°',
  //Also work for HeightUnits.METERS
  [DistanceUnits.METERS]: 'm',
  [DistanceUnits.KILOMETERS]: 'km',
  [DistanceUnits.MILES]: 'mi',
  [DistanceUnits.NAUTICAL_MILES]: 'NM',
  [HeightUnits.FEET]: 'ft',
  [SpeedUnits.KILOMETERS_PER_HOUR]: 'km/h',
  [SpeedUnits.METERS_PER_SECOND]: 'm/s',
  [SpeedUnits.MILES_PER_HOUR]: 'mph',
  [SpeedUnits.KNOTS]: 'kt',
  [OtherUnits.PERCENT]: '%',
  [WeightUnits.KILOGRAMS]: 'kg',
  [TemperatureUnits.CELCIUS]: '°C',
  [PressureUnits.HECTOPASCAL]: 'hPa',
  [VoltageUnits.VOLT]: 'V',
  [TimeUnits.MILLI_SECONDS]: 'ms',
  [TimeUnits.SECONDS]: 's',
  [TimeUnits.MINUTES]: 'min',
  [TimeUnits.HOURS]: 'h',
  [TimeUnits.DAYS]: 'd',
};

//Distance conversion constants
const METER_TO_MILE = 0.000621371;
const METER_TO_FEET = 3.28084;
const METER_TO_NAUTICAL_MILE = 0.000539957;

//Speed conversion constants
const MS_TO_KPH = 3.6;
const MS_TO_MPH = 2.23694;
const MS_TO_KNOTS = 1.94384;

//Time conversion constants
const S_TO_MS = 1000;
const MIN_TO_MS = 60000;
const HOURS_TO_MS = 3600000;
const DAYS_TO_MS = 86400000;

export function convertSpeedUnitFromMPerS(toUnit: string, value?: number | null, precision = 5): number | null {
  if (value === undefined || value === null || Number.isNaN(value)) {
    return null;
  }
  switch (toUnit) {
    case SpeedUnits.METERS_PER_SECOND:
      return Number(value.toFixed(precision));
    case SpeedUnits.KNOTS:
      return Number((value * MS_TO_KNOTS).toFixed(precision));
    case SpeedUnits.KILOMETERS_PER_HOUR:
      return Number((value * MS_TO_KPH).toFixed(precision));
    case SpeedUnits.MILES_PER_HOUR:
      return Number((value * MS_TO_MPH).toFixed(precision));
    default:
      return null;
  }
}

export function convertSpeedUnitToMPerS(fromUnit: string, value?: number | null, precision = 5): number | null {
  if (value === undefined || value === null || Number.isNaN(value)) {
    return null;
  }
  switch (fromUnit) {
    //Assuming speed is in m/s
    case SpeedUnits.METERS_PER_SECOND:
      return Number(value.toFixed(precision));
    case SpeedUnits.KNOTS:
      return Number((value / MS_TO_KNOTS).toFixed(precision));
    case SpeedUnits.KILOMETERS_PER_HOUR:
      return Number((value / MS_TO_KPH).toFixed(precision));
    case SpeedUnits.MILES_PER_HOUR:
      return Number((value / MS_TO_MPH).toFixed(precision));
    default:
      return null;
  }
}

export function convertDistanceUnitFromMeter(
  toUnit: string,
  valueInMeter?: number | null,
  precision = 5,
): number | null {
  if (valueInMeter === undefined || valueInMeter === null || Number.isNaN(valueInMeter)) {
    return null;
  }

  switch (toUnit) {
    //Assuming distance in meters
    case DistanceUnits.METERS:
      return Number(valueInMeter.toFixed(precision));
    case DistanceUnits.KILOMETERS:
      return Number((valueInMeter / 1000).toFixed(precision));
    case DistanceUnits.MILES:
      return Number((valueInMeter * METER_TO_MILE).toFixed(precision));
    case HeightUnits.FEET:
      return Number((valueInMeter * METER_TO_FEET).toFixed(precision));
    case DistanceUnits.NAUTICAL_MILES:
      return Number((valueInMeter * METER_TO_NAUTICAL_MILE).toFixed(precision));
    default:
      return null;
  }
}

export function convertDistanceUnitToMeter(fromUnit: string, value?: number | null, precision = 5): number | null {
  if (value === undefined || value === null || Number.isNaN(value)) {
    return null;
  }

  switch (fromUnit) {
    case DistanceUnits.METERS:
      return Number(value.toFixed(precision));
    case DistanceUnits.KILOMETERS:
      return Number((value * 1000).toFixed(precision));
    case DistanceUnits.MILES:
      return Number((value / METER_TO_MILE).toFixed(precision));
    case HeightUnits.FEET:
      return Number((value / METER_TO_FEET).toFixed(precision));
    case DistanceUnits.NAUTICAL_MILES:
      return Number((value / METER_TO_NAUTICAL_MILE).toFixed(precision));
    default:
      return null;
  }
}

export function convertTimeUnitFromMilliSeconds(
  toUnit: string,
  valueInMs?: number | null,
  precision = 5,
): number | null {
  if (valueInMs === undefined || valueInMs === null || Number.isNaN(valueInMs)) {
    return null;
  }

  switch (toUnit) {
    //Assuming time in milliseconds
    case TimeUnits.MILLI_SECONDS:
      return Number(valueInMs.toFixed(precision));
    case TimeUnits.SECONDS:
      return Number((valueInMs / S_TO_MS).toFixed(precision));
    case TimeUnits.MINUTES:
      return Number((valueInMs / MIN_TO_MS).toFixed(precision));
    case TimeUnits.HOURS:
      return Number((valueInMs / HOURS_TO_MS).toFixed(precision));
    case TimeUnits.DAYS:
      return Number((valueInMs / DAYS_TO_MS).toFixed(precision));
    default:
      return null;
  }
}

export function convertTimeUnitToMilliSeconds(fromUnit: string, value?: number | null, precision = 5): number | null {
  if (value === undefined || value === null || Number.isNaN(value)) {
    return null;
  }

  switch (fromUnit) {
    //Assuming time in milliseconds
    case TimeUnits.MILLI_SECONDS:
      return Number(value.toFixed(precision));
    case TimeUnits.SECONDS:
      return Number((value * S_TO_MS).toFixed(precision));
    case TimeUnits.MINUTES:
      return Number((value * MIN_TO_MS).toFixed(precision));
    case TimeUnits.HOURS:
      return Number((value * HOURS_TO_MS).toFixed(precision));
    case TimeUnits.DAYS:
      return Number((value * DAYS_TO_MS).toFixed(precision));
    default:
      return null;
  }
}

export function getConversionFunctions(unitType: UnitTypes): {
  convert: (toUnit: string, valueInMeter?: number, precision?: number) => number | null;
  convertBack: (fromUnit: string, value?: number, precision?: number) => number | null;
} {
  switch (unitType) {
    case UnitTypes.SPEED:
      return { convert: convertSpeedUnitFromMPerS, convertBack: convertSpeedUnitToMPerS };
    case UnitTypes.TIME:
      return { convert: convertTimeUnitFromMilliSeconds, convertBack: convertTimeUnitToMilliSeconds };
    case UnitTypes.DISTANCE:
    case UnitTypes.HEIGHT:
    default:
      return { convert: convertDistanceUnitFromMeter, convertBack: convertDistanceUnitToMeter };
  }
}

const unitWithoutSpace = [OtherUnits.PERCENT, TemperatureUnits.CELCIUS, AngleUnits.TURN] as Units[];

export function formatUnit(
  unit: Units,
  value?: number | null,
  options?: { precision?: number; showPlusSign?: boolean; prefix?: string },
) {
  const { precision, showPlusSign, prefix } = {
    precision: 0,
    showPlusSign: false,
    ...options,
  };
  if (value === undefined || value === null || Number.isNaN(value)) {
    return appIntl().formatMessage({ id: 'global.NA' });
  }
  let sign = '';
  if (showPlusSign) {
    sign = value > 0 ? '+' : '';
  }
  const prefixLabel = prefix ? `${prefix} ` : '';
  const unitLabelSpace = unitWithoutSpace.includes(unit) ? '' : ' ';
  return `${prefixLabel}${sign}${Number.parseFloat(value.toFixed(precision))}${unitLabelSpace}${unitLabel[unit]}`;
}

export function formatFrequencyWithUnit(frequency: number) {
  for (const unit of ['Hz', 'kHz', 'MHz', 'GHz']) {
    if (frequency < 1000) {
      return `${frequency.toFixed(frequency > 10 ? 0 : 1)} ${unit}`;
    }
    frequency = frequency / 1000;
  }
  return `${frequency.toFixed(frequency > 10 ? 0 : 1)} THz`;
}

export const MIN_NM = 0.01;

export function formatNauticalMiles(value?: number | null) {
  if (value === undefined || value === null) {
    return appIntl().formatMessage({ id: 'global.NA' });
  }
  if (value < MIN_NM) {
    return `<${MIN_NM} NM`;
  }
  return formatUnit(DistanceUnits.NAUTICAL_MILES, value, { precision: 2 });
}
